import { useCallback, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import "./App.css";
import AppNavbar from "./AppNavbar.js";
import SectionCard from "./SectionCard.js";
import { catalogFetch } from "./catalogApi.js";
import { communityFetch } from "./communityApi.js";
import {
  buildLoginUrl,
  buildLogoutUrl,
  buildSignupUrl,
  getTokenStorageKey,
  isTokenExpired,
  readTokenFromUrlHash,
} from "./auth.js";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || "";
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || "";
const COGNITO_REDIRECT_URI = `${window.location.origin}/`;
const COGNITO_LOGOUT_URI = `${window.location.origin}/`;

const INITIAL_BINARY_FORM = {
  title: "",
  yes_price: "0.50",
  community_ids: [],
};

const INITIAL_CATEGORICAL_FORM = {
  question: "",
  yes_price: "0.50",
  community_ids: [],
  options: ["", ""],
};

function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

function buildGeneratedId(prefix) {
  const fallback = `generated-${Date.now()}-${Math.floor(Math.random() * 1000000)}`;
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`.toLowerCase();
  }
  return `${prefix}-${fallback}`.toLowerCase();
}

function CreateMarketPage() {
  const [idToken, setIdToken] = useState("");
  const [me, setMe] = useState(null);
  const [balance, setBalance] = useState({ cash_available: 0, cash_reserved: 0 });
  const [approvedCommunities, setApprovedCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [marketType, setMarketType] = useState("BINARY");
  const [binaryForm, setBinaryForm] = useState(INITIAL_BINARY_FORM);
  const [categoricalForm, setCategoricalForm] = useState(INITIAL_CATEGORICAL_FORM);

  useEffect(() => {
    const tokenFromHash = readTokenFromUrlHash();
    const storedToken = localStorage.getItem(getTokenStorageKey()) || "";
    const token = tokenFromHash || storedToken;

    if (!token || isTokenExpired(token)) {
      localStorage.removeItem(getTokenStorageKey());
      setIdToken("");
      return;
    }

    localStorage.setItem(getTokenStorageKey(), token);
    setIdToken(token);
    if (tokenFromHash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const loginUrl = useMemo(
    () => buildLoginUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const signupUrl = useMemo(
    () => buildSignupUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_REDIRECT_URI),
    []
  );
  const logoutUrl = useMemo(
    () => buildLogoutUrl(COGNITO_DOMAIN, COGNITO_CLIENT_ID, COGNITO_LOGOUT_URI),
    []
  );

  const apiFetch = useCallback(async (path) => {
    if (!API_BASE_URL) {
      throw new Error("Missing REACT_APP_API_BASE_URL");
    }
    if (!idToken) {
      throw new Error("Not signed in");
    }
    const response = await fetch(`${API_BASE_URL}${path}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
    });
    const body = await response.json();
    if (!response.ok) {
      throw new Error(body.error || body.message || "Request failed");
    }
    return body;
  }, [idToken]);

  useEffect(() => {
    let cancelled = false;
    const loadAccountHeader = async () => {
      if (!idToken) {
        setMe(null);
        setBalance({ cash_available: 0, cash_reserved: 0 });
        setApprovedCommunities([]);
        setError("");
        return;
      }
      setLoading(true);
      setError("");
      try {
        const [meRes, balanceRes, myCommunitiesRes] = await Promise.all([
          apiFetch("/me"),
          apiFetch("/me/balance"),
          communityFetch("/me/communities", idToken),
        ]);
        if (!cancelled) {
          setMe(meRes);
          setBalance(balanceRes);
          const approvedItems = Array.isArray(myCommunitiesRes.items)
            ? myCommunitiesRes.items.filter(
                (item) => String(item.membership_status || "").toUpperCase() === "APPROVED"
              )
            : [];
          setApprovedCommunities(approvedItems);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load account");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadAccountHeader();
    return () => {
      cancelled = true;
    };
  }, [idToken, apiFetch]);

  const handleLogin = () => {
    if (!loginUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(loginUrl);
  };

  const handleSignup = () => {
    if (!signupUrl) {
      setError("Missing Cognito configuration in React env vars");
      return;
    }
    window.location.assign(signupUrl);
  };

  const handleLogout = () => {
    localStorage.removeItem(getTokenStorageKey());
    setIdToken("");
    setMe(null);
    setBalance({ cash_available: 0, cash_reserved: 0 });
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    }
  };

  const handleBinaryInputChange = (event) => {
    const { name, value } = event.target;
    setBinaryForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoricalInputChange = (event) => {
    const { name, value } = event.target;
    setCategoricalForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCommunitySelectionChange = (event) => {
    const selectedValues = Array.from(event.target.selectedOptions, (option) => option.value);
    if (marketType === "BINARY") {
      setBinaryForm((prev) => ({ ...prev, community_ids: selectedValues }));
      return;
    }
    setCategoricalForm((prev) => ({ ...prev, community_ids: selectedValues }));
  };

  const handleOptionChange = (index, value) => {
    setCategoricalForm((prev) => {
      const nextOptions = [...prev.options];
      nextOptions[index] = value;
      return { ...prev, options: nextOptions };
    });
  };

  const handleAddOption = () => {
    setCategoricalForm((prev) => ({
      ...prev,
      options: [...prev.options, ""],
    }));
  };

  const handleRemoveOption = (index) => {
    setCategoricalForm((prev) => {
      if (prev.options.length <= 2) {
        return prev;
      }
      return {
        ...prev,
        options: prev.options.filter((_, optionIndex) => optionIndex !== index),
      };
    });
  };

  const categoricalPreview = useMemo(() => {
    const question = categoricalForm.question.trim();
    const optionEntries = categoricalForm.options
      .map((option, index) => ({
        option: option.trim(),
        index,
      }))
      .filter((entry) => entry.option);

    const groupSeed = slugify(question) || "categorical-group";
    return optionEntries.map((entry) => ({
      marketId: `${groupSeed}-${slugify(entry.option) || `option-${entry.index + 1}`}`,
      optionLabel: entry.option,
      order: entry.index + 1,
    }));
  }, [categoricalForm.options, categoricalForm.question]);

  const createBinaryMarket = async () => {
    const title = binaryForm.title.trim();
    if (!title) {
      throw new Error("title is required");
    }

    const yesPrice = Number(binaryForm.yes_price);
    if (!(yesPrice > 0 && yesPrice < 1)) {
      throw new Error("yes_price must be between 0 and 1");
    }

    const marketId = buildGeneratedId("market");
    const payload = {
      market_id: marketId,
      title,
      group_id: marketId,
      group_title: title,
      option_label: title,
      category: "binary",
      status: "OPEN",
      outcomes: ["YES", "NO"],
      yes_price: yesPrice,
      qty: 0,
    };
    if (binaryForm.community_ids.length > 0) {
      payload.community_ids = binaryForm.community_ids;
    }

    await catalogFetch("/markets", idToken, {
      method: "POST",
      body: JSON.stringify(payload),
    });

    setSubmitSuccess(`Created market: ${marketId}`);
    setBinaryForm(INITIAL_BINARY_FORM);
  };

  const createCategoricalMarkets = async () => {
    const question = categoricalForm.question.trim();
    if (!question) {
      throw new Error("question is required");
    }

    const yesPrice = Number(categoricalForm.yes_price);
    if (!(yesPrice > 0 && yesPrice < 1)) {
      throw new Error("yes_price must be between 0 and 1");
    }

    const normalizedOptions = categoricalForm.options
      .map((option) => option.trim())
      .filter(Boolean);
    if (normalizedOptions.length < 2) {
      throw new Error("categorical markets require at least 2 options");
    }

    const uniqueOptionCount = new Set(
      normalizedOptions.map((option) => option.toLowerCase())
    ).size;
    if (uniqueOptionCount !== normalizedOptions.length) {
      throw new Error("options must be unique");
    }

    const groupId = buildGeneratedId("category");
    const communityIds = categoricalForm.community_ids;
    const createdMarketIds = [];

    for (const [index, optionLabel] of normalizedOptions.entries()) {
      const optionSlug = slugify(optionLabel) || `option-${index + 1}`;
      const payload = {
        market_id: `${groupId}-${optionSlug}`,
        title: optionLabel,
        group_id: groupId,
        group_title: question,
        option_label: optionLabel,
        group_order: index,
        category: "categorical",
        status: "OPEN",
        outcomes: ["YES", "NO"],
        yes_price: yesPrice,
        qty: 0,
      };
      if (communityIds.length > 0) {
        payload.community_ids = communityIds;
      }

      await catalogFetch("/markets", idToken, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      createdMarketIds.push(payload.market_id);
    }

    setSubmitSuccess(
      `Created categorical group "${question}" with ${createdMarketIds.length} options.`
    );
    setCategoricalForm(INITIAL_CATEGORICAL_FORM);
  };

  const handleCreateMarket = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");
    setSubmitting(true);

    try {
      if (marketType === "BINARY") {
        await createBinaryMarket();
      } else {
        await createCategoricalMarkets();
      }
    } catch (err) {
      setSubmitError(err.message || "Failed to create market");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCommunityIds = marketType === "BINARY"
    ? binaryForm.community_ids
    : categoricalForm.community_ids;

  return (
    <div className="App">
      <AppNavbar
        links={[
          { href: "/", label: "Home" },
          { href: "/open-orders", label: "Open Orders" },
          { href: "/communities", label: "Communities" },
          { href: "/leaderboard", label: "Leaderboard" },
          { href: "/market-resolution", label: "Resolve Market" },
          { href: "/markets/new", label: "Create Market" },
        ]}
        isSignedIn={Boolean(idToken)}
        me={me}
        balance={balance}
        onLogin={handleLogin}
        onLogout={handleLogout}
        onSignup={handleSignup}
      />
      <Container className="py-4">
        {error && (
          <Row className="mb-3">
            <Col>
              <Alert variant="danger" className="mb-0">{error}</Alert>
            </Col>
          </Row>
        )}
        {!idToken ? (
          <Row>
            <Col>
              <Alert variant="info" className="mb-0">
                Sign in with Cognito to create markets.
              </Alert>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col>
              <SectionCard title="Create Market">
                {submitError && <Alert variant="danger">{submitError}</Alert>}
                {submitSuccess && <Alert variant="success">{submitSuccess}</Alert>}
                {approvedCommunities.length === 0 && (
                  <Alert variant="secondary">
                    You are not an approved member of any communities yet. Leave the community picker empty to create a public market.
                  </Alert>
                )}

                <Form onSubmit={handleCreateMarket}>
                  <Form.Group as={Row} className="mb-4 align-items-center">
                    <Form.Label column sm={3} className="text-start">
                      Market Type
                    </Form.Label>
                    <Col sm={9}>
                      <div className="market-type-toggle">
                        <Button
                          type="button"
                          variant={marketType === "BINARY" ? "light" : "outline-light"}
                          disabled={submitting}
                          onClick={() => setMarketType("BINARY")}
                        >
                          Binary
                        </Button>
                        <Button
                          type="button"
                          variant={marketType === "CATEGORICAL" ? "light" : "outline-light"}
                          disabled={submitting}
                          onClick={() => setMarketType("CATEGORICAL")}
                        >
                          Categorical
                        </Button>
                      </div>
                    </Col>
                  </Form.Group>

                  {marketType === "BINARY" ? (
                    <>
                      <p className="market-create-hint">
                        Binary markets create a single YES/NO market.
                      </p>
                      <Form.Group as={Row} className="mb-3 align-items-center">
                        <Form.Label column sm={3} className="text-start">
                          Title
                        </Form.Label>
                        <Col sm={9}>
                          <Form.Control
                            name="title"
                            placeholder="Will BTC close above 120k by year end?"
                            value={binaryForm.title}
                            onChange={handleBinaryInputChange}
                            disabled={submitting}
                          />
                        </Col>
                      </Form.Group>
                    </>
                  ) : (
                    <>
                      <p className="market-create-hint">
                        Categorical markets create one market per option, all grouped under the same question. Options cannot be added later.
                      </p>
                      <Form.Group as={Row} className="mb-3 align-items-center">
                        <Form.Label column sm={3} className="text-start">
                          Question
                        </Form.Label>
                        <Col sm={9}>
                          <Form.Control
                            name="question"
                            placeholder="Who will win the MVP award?"
                            value={categoricalForm.question}
                            onChange={handleCategoricalInputChange}
                            disabled={submitting}
                          />
                        </Col>
                      </Form.Group>

                      <Form.Group as={Row} className="mb-3 align-items-start">
                        <Form.Label column sm={3} className="text-start">
                          Options
                        </Form.Label>
                        <Col sm={9}>
                          <div className="categorical-options-list">
                            {categoricalForm.options.map((option, index) => (
                              <div key={`option-${index}`} className="categorical-option-row">
                                <Form.Control
                                  value={option}
                                  onChange={(event) => handleOptionChange(index, event.target.value)}
                                  disabled={submitting}
                                  placeholder={`Option ${index + 1}`}
                                />
                                <Button
                                  type="button"
                                  variant="outline-danger"
                                  disabled={submitting || categoricalForm.options.length <= 2}
                                  onClick={() => handleRemoveOption(index)}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                          <div className="categorical-options-actions">
                            <Button
                              type="button"
                              variant="outline-light"
                              onClick={handleAddOption}
                              disabled={submitting}
                            >
                              Add Option
                            </Button>
                          </div>
                        </Col>
                      </Form.Group>
                    </>
                  )}

                  <Form.Group as={Row} className="mb-3 align-items-center">
                    <Form.Label column sm={3} className="text-start">
                      YES Price
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Control
                        name="yes_price"
                        placeholder="Starting price"
                        type="number"
                        min="0.01"
                        max="0.99"
                        step="0.01"
                        value={marketType === "BINARY" ? binaryForm.yes_price : categoricalForm.yes_price}
                        onChange={marketType === "BINARY" ? handleBinaryInputChange : handleCategoricalInputChange}
                        disabled={submitting}
                      />
                      <Form.Text className="text-muted">
                        This starting price is applied to every market created in this submission.
                      </Form.Text>
                    </Col>
                  </Form.Group>

                  <Form.Group as={Row} className="mb-3 align-items-start">
                    <Form.Label column sm={3} className="text-start">
                      Communities
                    </Form.Label>
                    <Col sm={9}>
                      <Form.Select
                        multiple
                        value={selectedCommunityIds}
                        onChange={handleCommunitySelectionChange}
                        disabled={submitting || approvedCommunities.length === 0}
                        style={{ minHeight: "140px" }}
                      >
                        {approvedCommunities.map((community) => (
                          <option key={community.community_id} value={community.community_id}>
                            {community.name}
                            {community.visibility ? ` (${community.visibility})` : ""}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Text className="text-muted">
                        Leave empty to create a public market. Hold Command on Mac or Ctrl on Windows to select multiple communities.
                      </Form.Text>
                    </Col>
                  </Form.Group>

                  {marketType === "CATEGORICAL" && (
                    <div className="categorical-preview">
                      <h4>Preview</h4>
                      <div className="categorical-preview-list">
                        {categoricalPreview.length === 0 ? (
                          <span className="categorical-preview-empty">
                            Add at least two option names to preview the categorical group.
                          </span>
                        ) : (
                          categoricalPreview.map((entry) => (
                            <div key={entry.marketId} className="categorical-preview-row">
                              <span>{entry.order}. {entry.optionLabel}</span>
                              <code>{entry.marketId}</code>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  <Row>
                    <Col sm={{ span: 9, offset: 3 }} className="d-grid">
                      <Button type="submit" disabled={submitting || loading}>
                        {submitting
                          ? (marketType === "BINARY" ? "Creating market..." : "Creating group...")
                          : (marketType === "BINARY" ? "Create Binary Market" : "Create Categorical Group")}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </SectionCard>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
}

export default CreateMarketPage;
